/**
 * Link Utilities
 * Helper functions for parsing and detecting link types
 */

/**
 * Parses a link to detect if it's a form link
 * @param href - The link to parse
 * @param eventId - The current event ID (optional, for stricter matching)
 * @returns Object with isFormLink boolean and formId number if applicable
 */
export function parseFormLink(href: string, eventId?: number): { isFormLink: boolean; formId: number | null } {
  if (!href || typeof href !== "string") {
    return { isFormLink: false, formId: null };
  }

  // Pattern: /{eventId}/form/{formId} or /{any-number}/form/{any-number}
  const formLinkPattern = /\/(\d+)\/form\/(\d+)/;
  const match = href.match(formLinkPattern);

  if (!match) {
    return { isFormLink: false, formId: null };
  }

  const linkEventId = parseInt(match[1], 10);
  const formId = parseInt(match[2], 10);

  // If eventId provided, verify it matches
  if (eventId !== undefined && linkEventId !== eventId) {
    return { isFormLink: false, formId: null };
  }

  return { isFormLink: true, formId };
}

/**
 * Parses a link to detect if it's a hotel booking link
 * @param href - The link to parse
 * @param eventId - The current event ID (optional, for stricter matching)
 * @returns Object with isHotelBookingLink boolean and bookingLinkId number if applicable
 */
export function parseHotelBookingLink(
  href: string,
  eventId?: number
): { isHotelBookingLink: boolean; bookingLinkId: number | null } {
  if (!href || typeof href !== "string") {
    return { isHotelBookingLink: false, bookingLinkId: null };
  }

  // Pattern: /{eventId}/hotel-booking/{bookingLinkId}
  const pattern = /\/(\d+)\/hotel-booking\/(\d+)/;
  const match = href.match(pattern);

  if (!match) {
    return { isHotelBookingLink: false, bookingLinkId: null };
  }

  const linkEventId = parseInt(match[1], 10);
  const bookingLinkId = parseInt(match[2], 10);

  if (eventId !== undefined && linkEventId !== eventId) {
    return { isHotelBookingLink: false, bookingLinkId: null };
  }

  return { isHotelBookingLink: true, bookingLinkId };
}

/**
 * Checks if a link is an anchor link (starts with #)
 */
export function isAnchorLink(href: string): boolean {
  return typeof href === "string" && href.startsWith("#");
}

/**
 * Checks if a link is an external link (starts with http:// or https://)
 */
export function isExternalLink(href: string): boolean {
  if (!href || typeof href !== "string") return false;
  return href.startsWith("http://") || href.startsWith("https://");
}
